import { usePolicyContext } from "../Contexts/AddPolicyContext";
import { baseUrl } from "../utilities/configurations";
import PolicyAliasModal from "./PolicyAliasModal";
import { useAuth } from "../Contexts/AuthContext";
import apiRequest from "../utilities/request";
import { useState, useEffect } from "react";
import SuccessModal from "./SuccessModal";
import InputField from "./InputField";
import PropTypes from "prop-types";
import Button from "./Button";

const EditAliasPolicyModal = ({ onSuccess }) => {
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const { setUserData } = useAuth();
  const {
    formatEditAliasPolicyValidation,
    setEditAliasPolicy,
    editAliasPolicy,
    selectedPolicy,
    setShowModal,
    setIsLoading,
  } = usePolicyContext();

  useEffect(() => {
    const fetchPolicy = async () => {
      try {
        const res = await apiRequest(`${baseUrl}/policy/policies`, "GET");
        if (res && !res.error) {
          setUserData(res.data || []);
        } else {
          console.error("Failed to load policies");
        }
      } catch (error) {
        console.error("Error fetching policies:", error);
      }
    };

    fetchPolicy();
  }, [setUserData]);

  const handleSaveChanges = async () => {
    if (!selectedPolicy) return;

    setIsLoading(true);
    try {
      const response = await apiRequest(
        `${baseUrl}/policy/edit-policy-alias`,
        "PATCH",
        {
          alias: editAliasPolicy,
          cardNo: selectedPolicy.cardNo,
        }
      );

      if (response?.status === true) {
        if (response?.data === "Policy alias updated.") {
          setShowSuccessModal(true);
          setEditAliasPolicy("");
        } else if (response?.data === "Policy alias already set.") {
          setShowErrorModal(true);
          setEditAliasPolicy("");
        }
      } else {
        console.error("Unexpected response status:", response);
      }
    } catch (err) {
      console.error("Error during API request:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setShowSuccessModal(false);
    setEditAliasPolicy("");
    setShowModal(false);
    if (onSuccess) onSuccess();
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      if (editAliasPolicy !== "") handleSaveChanges();
    } else if (event.key === "Escape") {
      handleClose();
    }
  };

  return (
    <div
      style={{ top: 0, left: 0, bottom: 0, right: 0 }}
      tabIndex={-1}
      onKeyDown={handleKeyDown}
      className="flex flex-col items-center justify-center min-h-screen max-w-screen fixed bg-black bg-opacity-20 backdrop-blur-md z-50 lg:pl-[240px]"
    >
      <div className="sm:w-89.5 w-72 bg-white m-auto rounded-2xl p-6">
        <div className="w-full flex flex-col">
          <div className="font-semibold text-lg text-[#1F2328]">
            Edit Policy Card Alias
          </div>

          <div className="mt-1 text-[#383F49]">
            Customize your policy card alias
          </div>

          <div className="my-5">
            <div className="mb-1 font-semibold text-[#1F2328]">
              Policy Alias
            </div>
            <InputField
              placeholder="Primary policy"
              value={editAliasPolicy}
              onChange={formatEditAliasPolicyValidation}
            />
          </div>
        </div>
        <div className="flex flex-row">
          <Button
            className="w-full h-10 rounded-xl font-semibold mt-2 bg-white text-[#383F49] border-2 border-[#D6D7D8] transition-transform hover:scale-105"
            onClick={handleClose}
          >
            Cancel
          </Button>
          <Button
            className={`w-full h-10 rounded-xl font-semibold mt-2 ml-2 ${
              editAliasPolicy === ""
                ? "text-[#A3A7AB] bg-[#F6F7F7]"
                : "text-white bg-[#0947AB] transition-transform hover:scale-105"
            }`}
            disabled={editAliasPolicy === ""}
            onClick={handleSaveChanges}
          >
            Save Changes
          </Button>
        </div>
      </div>
      {showSuccessModal && (
        <SuccessModal
          isOpen={showSuccessModal}
          title="Alias Successfully Updated"
          body="The policy alias has been updated with the new alias."
          onClick={handleClose}
        />
      )}

      {showErrorModal && (
        <PolicyAliasModal
          isOpen={showErrorModal}
          title="Alias Already In Use"
          body="The policy alias you entered is already being used. Please choose a different alias."
          onClick={() => setShowErrorModal(false)}
        />
      )}
    </div>
  );
};

EditAliasPolicyModal.propTypes = {
  onSuccess: PropTypes.func,
};

export default EditAliasPolicyModal;
